import { useCallback } from 'react';
import * as THREE from 'three';
import { Box3, Vector3 } from 'three';
import { Assembly } from 'src/services/assemblies';
import { isAccesory } from 'src/utils/configurator.utils';
import { checkStock } from 'src/services/stock';
import { getDimensionsWithGeometry } from 'src/utils/3d.utils';
import { getPiecePrice, getPriceWithSize } from './usePricing';
import {
  computeBoundingBox,
  computeBoundingBoxWithOffset,
  getDimensions,
  getFaceName,
  makeExtrudeSettings,
  PatternsMap,
  PieceDetail,
  ZERO_FACE_DETAILS,
} from './useConfigurator.utils';

/**
 * Hook qui gère le calcul des détails de l'assemblage :
 * prix, dimensions, stock pour chaque pièce/stèle/placage/semelle/coffret.
 */
export const useAssemblyDetails = (
  pieces: any[],
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  patterns: PatternsMap,
  assembly: Assembly | null,
  settings: { minPriceFrench?: number; minPriceImport?: number } | null,
  adminEngravingsPrice: any,
) => {
  const assemblyUser = assembly?.quote?.user;

  const checkStockMutation = useCallback(async () => {
    const stockPieces = pieces.map((piece) => {
      const activePiece = activePieces.find((p) => p.id === piece.id);

      const { x, y, z } = getDimensionsWithGeometry(
        piece.geometry.attributes.position.array,
      );

      const weight = Math.round(
        (activePiece?.granit?.weight ?? 0) * ((x * y * z) / 100),
      );

      return {
        pieceId: piece.id ?? -1,
        granitId: activePiece.granit?.id ?? '',
        newGeometry: [...piece.geometry.attributes.position.array],
        x,
        y,
        z,
        weight,
      };
    });

    const stockSteles = customSteles.map((stele) => {
      return {
        ...stele,
        granitId: stele.granit?.id ?? '',
      };
    });

    const data = await checkStock({
      stockPieces,
      stockSteles,
    });

    return data;
  }, [pieces, activePieces, customSteles]);

  const getAssemblyDetails = async () => {
    const stocks = await checkStockMutation();

    const piecesDetails = pieces.map((el, elIndex) => {
      const pieceBox = computeBoundingBox(
        el.geometry.attributes.position.array,
      );
      const { x, y, z, size } = getDimensions(pieceBox);

      const activePiece = activePieces.find((p) => p.id === el.id);
      const granitId = activePiece?.granit?.id ?? '';

      const stockPiece = stocks.stockPieces.find(
        (s) => s.pieceId === el.id && s.granitId === granitId,
      );

      const assemblyStockPiece = stocks.assemblyStock?.stockPieces?.find(
        (s) => s.pieceId === el.id && s.granitId === granitId,
      );

      let stockToTake = null;

      if (assemblyStockPiece) {
        stockToTake = assemblyStockPiece;
      } else {
        stockToTake = stockPiece;
      }

      const { ttc, ht, purchase } = getPiecePrice(
        activePieces[elIndex],
        x,
        y,
        z,
        assemblyUser,
        assembly?.type,
        settings,
        stockToTake
          ? {
              price: stockToTake.price,
            }
          : undefined,
      );

      return {
        id: activePieces[elIndex]?.id,
        uuid: activePieces[elIndex]?.uuid,
        underAssembly: activePieces[elIndex]?.underAssembly,
        geometry: el.geometry,
        position: el.position,
        name: activePieces[elIndex]?.name,
        size,
        granit: activePieces[elIndex]?.granit,
        ttcPrice: ttc,
        htPrice: ht,
        purchasePrice: purchase,
        type: activePieces[elIndex].type,
        x,
        y,
        z,
        stockId: stockToTake?.stockId,
        stockType: assemblyStockPiece ? 'assembly' : 'piece',
      };
    });

    const underAssemblyPieces = piecesDetails.reduce((acc: any, piece) => {
      if (piece.underAssembly) {
        const uuid = piece.underAssembly.uuid;
        if (!acc[uuid]) {
          acc[uuid] = {
            underAssemblyUUID: uuid,
            underAssemblyName: piece.underAssembly.name,
            pieces: [],
          };
        }
        acc[uuid].pieces.push(piece);
      }
      return acc;
    }, {});

    const underAssemblies: {
      pieces: PieceDetail[];
      underAssemblyName: string;
      underAssemblyUUID: string;
    }[] = Object.values(underAssemblyPieces);

    const simplePieces: PieceDetail[] = piecesDetails.filter(
      (piece) => !piece.underAssembly && !isAccesory(piece.type),
    );
    const accesories: PieceDetail[] = piecesDetails.filter(
      (piece) => !piece.underAssembly && isAccesory(piece.type),
    );

    // --- Stèles ---
    const stelesDetails: PieceDetail[] = customSteles.map((el, elIndex) => {
      const geometry = new THREE.ExtrudeGeometry(el.shape, el.extrudeSettings);

      const pieceBox = computeBoundingBox(
        (geometry.attributes.position as any).array,
      );
      const { x, y, z, size } = getDimensions(pieceBox);

      const stock = stocks.stockSteles.find((s) => {
        return (
          s.stele.baseIndex === el.baseIndex &&
          s.stele.boxDownWidth === el.boxDownWidth &&
          s.stele.boxHeight === el.boxHeight &&
          s.stele.boxLeftHeight === el.boxLeftHeight &&
          s.stele.boxRightHeight === el.boxRightHeight &&
          s.stele.boxUpWidth === el.boxUpWidth &&
          s.stele.headIndex === el.headIndex &&
          s.stele.depth === el.depth &&
          s.granitId === el.granit?.id
        );
      });

      const { ttc, ht, purchase } = getPiecePrice(
        {
          polishingX: el.additionalPriceForCustomStele?.polishingX ?? 0,
          polishingY: el.additionalPriceForCustomStele?.polishingY ?? 0,
          polishingZ: el.additionalPriceForCustomStele?.polishingZ ?? 0,
          sawingX: el.additionalPriceForCustomStele?.sawingX ?? 0,
          sawingY: el.additionalPriceForCustomStele?.sawingY ?? 0,
          sawingZ: el.additionalPriceForCustomStele?.sawingZ ?? 0,
          importExtra: 0,
          frenchExtra: el.additionalPriceForCustomStele?.frenchExtra ?? 0,
          granit: el.granit,
          frenchLinearDetails: {
            faceX:
              el.additionalPriceForCustomStele?.frenchLinearDetails.faceX ?? 0,
            faceX2:
              el.additionalPriceForCustomStele?.frenchLinearDetails.faceX2 ?? 0,
            faceY:
              el.additionalPriceForCustomStele?.frenchLinearDetails.faceY ?? 0,
            faceY2:
              el.additionalPriceForCustomStele?.frenchLinearDetails.faceY2 ?? 0,
            faceZ:
              el.additionalPriceForCustomStele?.frenchLinearDetails.faceZ ?? 0,
            faceZ2:
              el.additionalPriceForCustomStele?.frenchLinearDetails.faceZ2 ?? 0,
          },
          frenchM2Details: {
            faceX: el.additionalPriceForCustomStele?.frenchM2Details.faceX ?? 0,
            faceX2:
              el.additionalPriceForCustomStele?.frenchM2Details.faceX2 ?? 0,
            faceY: el.additionalPriceForCustomStele?.frenchM2Details.faceY ?? 0,
            faceY2:
              el.additionalPriceForCustomStele?.frenchM2Details.faceY2 ?? 0,
            faceZ: el.additionalPriceForCustomStele?.frenchM2Details.faceZ ?? 0,
            faceZ2:
              el.additionalPriceForCustomStele?.frenchM2Details.faceZ2 ?? 0,
          },
          importLinearDetails: ZERO_FACE_DETAILS,
          importM2Details: ZERO_FACE_DETAILS,
          type: 'STELE',
        },
        x,
        y,
        z,
        assemblyUser,
        assembly?.type,
        settings,
        stock,
      );
      return {
        uuid: el.id,
        name: `Stèle personnalisée ${elIndex + 1}`,
        size,
        geometry,
        granit: el?.granit,
        position: el.position,
        ttcPrice: ttc,
        htPrice: ht,
        purchasePrice: purchase,
        type: 'STELE',
        x,
        y,
        z,
      };
    });

    // --- Placages ---
    const platingsDetails: PieceDetail[] = customPlatings
      .filter((el) => el.isActive)
      .map((el, elIndex) => {
        const geometry = new THREE.ExtrudeGeometry(
          el.shape,
          makeExtrudeSettings(el.shapeDetails.depth),
        );

        const shapeDetails = customPlatings[0].shapeDetails;
        const slopeStart = shapeDetails.monumentFront;
        const slopeEnd = shapeDetails.monumentBack;

        const pieceBox = computeBoundingBox(
          (geometry.attributes.position as any).array,
        );
        const { x, y, z, size } = getDimensions(pieceBox);

        const { ttc, ht, purchase } = getPiecePrice(
          {
            polishingX: 1,
            polishingY: 1,
            polishingZ: 1,
            sawingX: 2,
            sawingY: 2,
            sawingZ: 2,
            importExtra: 0,
            frenchExtra: 0,
            granit: el.granit,
            frenchLinearDetails: ZERO_FACE_DETAILS,
            frenchM2Details: ZERO_FACE_DETAILS,
            importLinearDetails: ZERO_FACE_DETAILS,
            importM2Details: ZERO_FACE_DETAILS,
            type: 'PLATING',
          },
          x,
          y,
          z,
          assemblyUser,
          assembly?.type,
          settings,
        );

        return {
          uuid: el.uuid,
          name: `Placage ${getFaceName(el.key)}`,
          size,
          geometry,
          position: el.position,
          granit: el?.granit,
          ttcPrice: ttc,
          htPrice: ht,
          purchasePrice: purchase,
          type: 'PLATING',
          x,
          y,
          z,
          slope: shapeDetails.hasSlope
            ? {
                hasSlope: shapeDetails.hasSlope,
                orientation: shapeDetails.orientation,
                slopeStart,
                slopeEnd,
                mode: shapeDetails.mode,
              }
            : undefined,
        };
      });

    // --- Semelles ---
    const solesDetails: PieceDetail[] = customSoles
      .filter((el) => el.isActive)
      .map((el, elIndex) => {
        const geometry = new THREE.ExtrudeGeometry(
          el.shape,
          el.extrudeSettings,
        );

        const pieceBox = computeBoundingBox(
          (geometry.attributes.position as any).array,
        );
        const { x, y, z, size } = getDimensions(pieceBox);

        const { ttc, ht, purchase } = getPiecePrice(
          {
            polishingX: 1,
            polishingY: 1,
            polishingZ: 1,
            sawingX: 2,
            sawingY: 2,
            sawingZ: 2,
            importExtra: 0,
            frenchExtra: 0,
            granit: el.granit,
            frenchLinearDetails: ZERO_FACE_DETAILS,
            frenchM2Details: ZERO_FACE_DETAILS,
            importLinearDetails: ZERO_FACE_DETAILS,
            importM2Details: ZERO_FACE_DETAILS,
            type: 'SOLE',
          },
          x,
          y,
          z,
          assemblyUser,
          assembly?.type,
          settings,
        );

        return {
          uuid: el.uuid,
          name: `Semelle ${getFaceName(el.key)}`,
          size,
          geometry,
          position: el.position,
          granit: el?.granit,
          ttcPrice: ttc,
          htPrice: ht,
          purchasePrice: purchase,
          type: 'SOLE',
          x,
          y,
          z,
        };
      });

    // --- Coffrets ---
    const boxesDetails: PieceDetail[] = customBoxes
      .filter((el) => el.isActive)
      .map((el, elIndex) => {
        const geometry = new THREE.ExtrudeGeometry(
          el.shape,
          el.extrudeSettings,
        );

        const pieceBox = computeBoundingBox(
          (geometry.attributes.position as any).array,
        );

        const shapeDetails = customBoxes[0].shapeDetails;
        const slopeStart = shapeDetails.monumentFront;
        const slopeEnd = shapeDetails.monumentBack;

        const { x, y, z, size } = getDimensions(pieceBox);

        const { ttc, ht, purchase } = getPiecePrice(
          {
            polishingX: 1,
            polishingY: 1,
            polishingZ: 1,
            sawingX: 2,
            sawingY: 2,
            sawingZ: 2,
            importExtra: 0,
            frenchExtra: 0,
            granit: el.granit,
            frenchLinearDetails: ZERO_FACE_DETAILS,
            frenchM2Details: ZERO_FACE_DETAILS,
            importLinearDetails: ZERO_FACE_DETAILS,
            importM2Details: ZERO_FACE_DETAILS,
            type: 'BOX',
          },
          x,
          y,
          z,
          assemblyUser,
          assembly?.type,
          settings,
        );

        return {
          uuid: el.uuid,
          name: `Coffret ${getFaceName(el.key)}`,
          size,
          geometry,
          position: el.position,
          granit: el?.granit,
          ttcPrice: ttc,
          htPrice: ht,
          purchasePrice: purchase,
          type: 'BOX',
          x,
          y,
          z,
          slope: shapeDetails.hasSlope
            ? {
                hasSlope: shapeDetails.hasSlope,
                orientation: shapeDetails.orientation,
                slopeStart,
                slopeEnd,
                mode: shapeDetails.mode,
              }
            : undefined,
        };
      });

    // --- Sous-assemblages avec taille ---
    const underAssembliesWithSize = underAssemblies.map((underAssembly) => {
      const globalBox = new Box3();

      for (const [elIndex, el] of underAssembly.pieces.entries()) {
        const pieceBox = computeBoundingBoxWithOffset(
          el.geometry.attributes.position.array,
          el.position,
        );

        globalBox.union(pieceBox);
      }

      const x = Number.parseFloat(
        (globalBox.max.x - globalBox.min.x).toFixed(4),
      );
      const y = Number.parseFloat(
        (globalBox.max.y - globalBox.min.y).toFixed(4),
      );
      const z = Number.parseFloat(
        (globalBox.max.z - globalBox.min.z).toFixed(4),
      );
      const size = x * y * z;

      return {
        pieces: underAssembly.pieces,
        underAssemblyName: underAssembly.underAssemblyName,
        underAssemblyUUID: underAssembly.underAssemblyUUID,
        x,
        y,
        z,
        size,
      };
    });

    // --- Patterns & gravures ---
    let patternsDetails: any[] = [];

    for (const key1 in patterns) {
      for (const key2 in patterns[key1]) {
        const elements = patterns[key1][key2].elements;
        patternsDetails = [...patternsDetails, ...elements];
      }
    }

    const filteredPatterns = patternsDetails.filter(
      (el) => el.type === 'image',
    );
    const mapedPatterns = filteredPatterns.map((el) => {
      const defaultM3 = el.default.x * el.default.y;
      const actualM3 = el.scale.x * el.scale.y;

      let coeff = actualM3 / defaultM3;

      if (actualM3 < defaultM3) coeff = 1;

      let ttcPrice = Math.ceil(el.price * coeff);
      let htPrice = 0;

      // If it's a pattern created by an admin, we apply the coefficients
      if (el.user?.role === 'ADMIN') {
        ttcPrice =
          ttcPrice * (assemblyUser?.userCoefficients?.patterns ?? 1) * 1.2;
        htPrice = Math.ceil(el.price * coeff);
      }

      if (el.user?.role !== 'ADMIN' && el.leaderGranitEngraving) {
        htPrice = 0;
        // TODO add the price of the engraving
      }

      return {
        ...el,
        ttcPrice,
        htPrice,
      };
    });
    const engravings = patternsDetails.filter((el) => el.type === 'text');
    const mapedEngravings = engravings.map((el) => {
      const elPrice =
        (el.color === '#FFFFFF'
          ? getPriceWithSize(
              assemblyUser?.userCoefficients?.engravingWhite ?? 0,
              +el.fontSize,
            )
          : getPriceWithSize(
              assemblyUser?.userCoefficients?.engravingGold ?? 0,
              +el.fontSize,
            )) * (el?.text?.replace(/ /g, '').length ?? 1);

      let buyPrice = 0;
      if (adminEngravingsPrice) {
        buyPrice =
          (el.color === '#FFFFFF'
            ? getPriceWithSize(adminEngravingsPrice.white, +el.fontSize)
            : getPriceWithSize(adminEngravingsPrice.gold, +el.fontSize)) *
          (el?.text?.replace(/ /g, '').length ?? 1);
      }

      return {
        ...el,
        ttcPrice: Math.ceil(elPrice),
        htPrice: Math.ceil(buyPrice),
      };
    });

    return {
      underAssemblies: underAssembliesWithSize,
      pieces: simplePieces,
      steles: stelesDetails,
      accesories,
      patterns: mapedPatterns,
      engravings: mapedEngravings,
      platings: platingsDetails,
      soles: solesDetails,
      boxes: boxesDetails,
    };
  };

  const getAssemblySize = (wihoutSole = false) => {
    const globalBox = new THREE.Box3(
      new Vector3(0, 0, 0),
      new Vector3(0, 0, 0),
    );

    for (const [elIndex, el] of customSteles.entries()) {
      const geometry = new THREE.ExtrudeGeometry(el.shape, el.extrudeSettings);

      const pieceBox = computeBoundingBoxWithOffset(
        (geometry.attributes.position as any).array,
        el.position,
      );
      globalBox.union(pieceBox);
    }

    for (const [elIndex, el] of customPlatings
      .filter((el) => el.isActive)
      .entries()) {
      const geometry = new THREE.ExtrudeGeometry(
        el.shape,
        makeExtrudeSettings(el.shapeDetails.depth),
      );

      geometry.rotateY(el.shapeDetails.isRotated ? 1.57 : 0);
      const pieceBox = computeBoundingBoxWithOffset(
        (geometry.attributes.position as any).array,
        el.position,
      );

      globalBox.union(pieceBox);
    }

    const piecesToUse = wihoutSole
      ? pieces.filter((el, elIndex) => activePieces[elIndex].type !== 'SOLE')
      : pieces;

    for (const [elIndex, el] of piecesToUse.entries()) {
      const pieceBox = computeBoundingBoxWithOffset(
        el.geometry.attributes.position.array,
        el.position,
      );

      globalBox.union(pieceBox);
    }

    const x = Number.parseFloat((globalBox.max.x - globalBox.min.x).toFixed(4));
    const z = Number.parseFloat((globalBox.max.z - globalBox.min.z).toFixed(4));

    return {
      width: x,
      depth: z,
    };
  };

  return {
    getAssemblyDetails,
    getAssemblySize,
    checkStockMutation,
  };
};
